/**
 * The image work behind Settings, Brand, on plain RGBA pixels so it runs (and is tested) without a
 * browser. The canvas glue is in `logo-canvas.ts`.
 *
 * What the owner gets from one upload:
 *   - the box check: most logos owners have are a JPG with a white box, or a WhatsApp screenshot
 *     with a grey one. "Remove the background" clears the box colour from the edges inwards, so a
 *     white part inside the logo stays.
 *   - the trim: empty edges go, so the logo fills its place.
 *   - the square: the whole logo, or its left or middle part for a wide logo with a symbol.
 *   - the checks: what goes wrong in each place, in plain words, with a one-tap fix.
 */

export type Pixels = { data: Uint8ClampedArray; width: number; height: number };
export type Rgb = [number, number, number];
export type Box = { color: Rgb; white: boolean };
export type Rect = { x: number; y: number; width: number; height: number };

const distance = (data: Uint8ClampedArray, index: number, color: Rgb) =>
  Math.abs(data[index]! - color[0]) + Math.abs(data[index + 1]! - color[1]) + Math.abs(data[index + 2]! - color[2]);

/**
 * A solid box around the logo, read from eight points on the edges. Six or more must be opaque and
 * near one colour. Null for a logo on a transparent background.
 */
export function findBox({ data, width, height }: Pixels): Box | null {
  if (width < 3 || height < 3) return null;
  const right = width - 2;
  const bottom = height - 2;
  const midX = width >> 1;
  const midY = height >> 1;
  const points: Array<[number, number]> = [
    [1, 1], [right, 1], [1, bottom], [right, bottom],
    [midX, 1], [midX, bottom], [1, midY], [right, midY],
  ];
  const opaque: Rgb[] = [];
  for (const [x, y] of points) {
    const index = (y * width + x) * 4;
    if (data[index + 3]! > 200) opaque.push([data[index]!, data[index + 1]!, data[index + 2]!]);
  }
  if (opaque.length < 6) return null;
  const mean = [0, 1, 2].map((k) => opaque.reduce((sum, point) => sum + point[k as 0 | 1 | 2], 0) / opaque.length) as Rgb;
  const close = opaque.filter(
    (point) => Math.abs(point[0] - mean[0]) + Math.abs(point[1] - mean[1]) + Math.abs(point[2] - mean[2]) < 40,
  );
  if (close.length < 6) return null;
  const color = mean.map(Math.round) as Rgb;
  return { color, white: Math.min(...color) > 235 };
}

/**
 * The same pixels with the box cleared: a flood fill from every edge pixel through pixels close to
 * the box colour (or already clear). A white shape enclosed by the logo is not reached, so it stays.
 */
export function clearBox(pixels: Pixels, box: Box, tolerance = 60): Pixels {
  const { width, height } = pixels;
  const data = new Uint8ClampedArray(pixels.data);
  const seen = new Uint8Array(width * height);
  const stack: number[] = [];
  for (let x = 0; x < width; x += 1) stack.push(x, (height - 1) * width + x);
  for (let y = 0; y < height; y += 1) stack.push(y * width, y * width + width - 1);
  while (stack.length > 0) {
    const point = stack.pop()!;
    if (seen[point]) continue;
    seen[point] = 1;
    const index = point * 4;
    if (data[index + 3]! >= 20 && distance(data, index, box.color) >= tolerance) continue;
    data[index + 3] = 0;
    const x = point % width;
    if (x > 0) stack.push(point - 1);
    if (x < width - 1) stack.push(point + 1);
    if (point >= width) stack.push(point - width);
    if (point < width * (height - 1)) stack.push(point + width);
  }
  return { data, width, height };
}

/** The smallest rectangle that holds every visible pixel, with 2% of air. Null for an empty picture. */
export function visibleBounds({ data, width, height }: Pixels): Rect | null {
  let left = width;
  let top = height;
  let right = -1;
  let bottom = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3]! > 16) {
        if (x < left) left = x;
        if (x > right) right = x;
        if (y < top) top = y;
        if (y > bottom) bottom = y;
      }
    }
  }
  if (right < 0) return null;
  const air = Math.round(Math.max(right - left, bottom - top) * 0.02);
  left = Math.max(0, left - air);
  top = Math.max(0, top - air);
  right = Math.min(width - 1, right + air);
  bottom = Math.min(height - 1, bottom + air);
  return { x: left, y: top, width: right - left + 1, height: bottom - top + 1 };
}

/** Which part of a logo the square version shows. */
export type SquareFocus = "whole" | "left" | "middle";

/** A logo this much wider than tall offers the left and middle parts for its square. */
export const WIDE_LOGO = 1.3;

/**
 * The part of the logo (a `width` x `height` picture) that fills the square. "whole" is all of
 * it; "left" and "middle" are a square of the logo's height, for a wide logo.
 */
export function squareSource(width: number, height: number, focus: SquareFocus): Rect {
  if (focus === "whole" || width / height < WIDE_LOGO) return { x: 0, y: 0, width, height };
  const side = height;
  const x = focus === "left" ? 0 : Math.round((width - side) / 2);
  return { x, y: 0, width: side, height: side };
}

/**
 * Share of visible pixels that are near black or near white, and the most common strong colour.
 * Pixels in the box colour do not count, because the box is not the logo.
 */
export function inkStats(
  { data, width, height }: Pixels,
  box: Box | null,
): { darkShare: number; lightShare: number; accent: string | null } {
  let visible = 0;
  let dark = 0;
  let light = 0;
  const buckets = new Map<number, { count: number; r: number; g: number; b: number }>();
  const step = Math.max(1, Math.floor(Math.sqrt((width * height) / 40000)));
  for (let y = 0; y < height; y += step) {
    for (let x = 0; x < width; x += step) {
      const index = (y * width + x) * 4;
      if (data[index + 3]! < 128) continue;
      if (box && distance(data, index, box.color) < 60) continue;
      visible += 1;
      const r = data[index]!;
      const g = data[index + 1]!;
      const b = data[index + 2]!;
      const lum = luminance(r, g, b);
      if (lum < 0.05) dark += 1;
      if (lum > 0.8) light += 1;
      const max = Math.max(r, g, b) / 255;
      const min = Math.min(r, g, b) / 255;
      const lightness = (max + min) / 2;
      const saturation = max === min ? 0 : lightness > 0.5 ? (max - min) / (2 - max - min) : (max - min) / (max + min);
      if (saturation > 0.35 && lightness > 0.2 && lightness < 0.75) {
        const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
        const bucket = buckets.get(key) ?? { count: 0, r: 0, g: 0, b: 0 };
        bucket.count += 1;
        bucket.r += r;
        bucket.g += g;
        bucket.b += b;
        buckets.set(key, bucket);
      }
    }
  }
  let best: { count: number; r: number; g: number; b: number } | null = null;
  for (const bucket of buckets.values()) if (!best || bucket.count > best.count) best = bucket;
  const hex = (value: number) => Math.round(value).toString(16).padStart(2, "0");
  const accent =
    best && best.count > visible * 0.04 ? `#${hex(best.r / best.count)}${hex(best.g / best.count)}${hex(best.b / best.count)}` : null;
  return {
    darkShare: visible ? dark / visible : 0,
    lightShare: visible ? light / visible : 0,
    accent,
  };
}

function luminance(r: number, g: number, b: number): number {
  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

// --- the checks -------------------------------------------------------------------------------

/** What the browser found in the draft logo, after the owner's fixes. */
export type LogoFacts = {
  /** A box is still around the logo (not removed). */
  box: Box | null;
  /** The square is mostly near black or navy: it fades on a dark screen. */
  dark: boolean;
  /** The square is mostly near white: it fades on a white screen. */
  light: boolean;
  /** The file is under 300 px on its long side: blurry in large places. */
  small: boolean;
  /** A wide logo squeezed whole into the square: its words are too small to read. */
  tinyText: boolean;
  plateOnDark: boolean;
};

export type LogoFix = "background" | "plate" | "left";
export type PlaceId = "sidebar" | "member" | "dark" | "invite" | "sign-in" | "chips";
export type PlaceCheck = { place: PlaceId; problem: { message: string; fix: LogoFix | null } | null };

/** The file is blurry in large places under this many pixels on its long side. */
export const SMALL_LOGO_SIDE = 300;

/**
 * Each place, and the first thing that goes wrong there. Places with white ground hide a white box,
 * so only a box in another colour counts there.
 */
export function checkPlaces(facts: LogoFacts): PlaceCheck[] {
  const colouredBox = facts.box !== null && !facts.box.white;
  const boxProblem = { message: "A box shows around the logo.", fix: "background" as const };
  const tinyProblem = { message: "The words are too small to read in the square.", fix: "left" as const };
  const lightProblem = { message: "The logo is too light on a white screen.", fix: null };
  const blurProblem = { message: "The logo is blurry at this size. Upload a file of 512 px or more.", fix: null };

  const check = (place: PlaceId, ...rules: Array<[boolean, PlaceCheck["problem"]]>): PlaceCheck => ({
    place,
    problem: rules.find(([fails]) => fails)?.[1] ?? null,
  });

  return [
    check("sidebar", [colouredBox, boxProblem], [facts.tinyText, tinyProblem], [facts.light, lightProblem]),
    check("member", [colouredBox, boxProblem], [facts.tinyText, tinyProblem], [facts.light, lightProblem]),
    check(
      "dark",
      [facts.box !== null && !facts.plateOnDark, boxProblem],
      [facts.dark && !facts.plateOnDark, { message: "The logo is hard to see on a dark screen.", fix: "plate" }],
      [facts.tinyText, tinyProblem],
    ),
    check("invite", [facts.small, blurProblem], [colouredBox, boxProblem]),
    check("sign-in", [facts.small, blurProblem], [colouredBox, boxProblem], [facts.light, lightProblem]),
    check(
      "chips",
      [facts.box !== null, { message: "A box shows on the chosen chip.", fix: "background" }],
      [facts.tinyText, tinyProblem],
    ),
  ];
}
